import React, { useRef, useState } from "react";
import {
  CornerUpLeft,
  Clock,
  Activity,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Calendar,
  Zap,
  Users,
  Loader2,
  Loader2Icon,
  Download,
} from "lucide-react";
import { useGetPerformanceTestResults } from "../../../useQuery";
import { generatePerformanceReportPDF } from "../../../utils/generatePDFReport";

const PerformanceResults = ({ suite, onBack }) => {
  const suiteId = suite?.id || suite?.testSuiteId;
  const [expandedEndpoints, setExpandedEndpoints] = useState({});
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const reportRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  const {
    data: endpointUrlsResponse,
    isLoading: loadingUrls,
    error: urlsError,
  } = useGetPerformanceTestResults({
    testSuiteId: suiteId,
    enabled: !!suiteId,
  });

  const endpointUrls = endpointUrlsResponse?.content || [];

  // Fetch results for selected endpoint
  const { data: endpointResults, isLoading: loadingResults } = useGetPerformanceTestResults({
    testSuiteId: suiteId,
    url: selectedEndpoint,
    enabled: !!selectedEndpoint,
  });

  const toggleEndpoint = (url) => {
    setExpandedEndpoints((prev) => ({
      ...prev,
      [url]: !prev[url],
    }));
    if (!expandedEndpoints[url]) {
      setSelectedEndpoint(url);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-100 text-green-700 border-green-200";
      case "RUNNING":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "FAILED":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getLoadProfileBadge = (profile) => {
    const colors = {
      FIXED: "bg-purple-100 text-purple-700 border-purple-200",
      RAMP_UP: "bg-blue-100 text-blue-700 border-blue-200",
      SPIKE: "bg-orange-100 text-orange-700 border-orange-200",
    };
    return colors[profile] || "bg-gray-100 text-gray-700 border-gray-200";
  };

  const handleGeneratePDF = () => {
    generatePerformanceReportPDF(
      reportRef,
      suite?.testSuiteName,
      () => setIsGeneratingPDF(true),
      () => setIsGeneratingPDF(false),
      (error) => {
        console.error("Error generating PDF:", error);
        setIsGeneratingPDF(false);
      },
    );
  };

  if (loadingUrls) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
          <p className="ml-3 text-gray-500">Loading results...</p>
        </div>
      </div>
    );
  }

  if (urlsError) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="flex items-center justify-center h-full">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="ml-3 text-red-600">Failed to load results</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 cursor-pointer"
          >
            <CornerUpLeft size={20} />
          </button>

          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-900">Performance Test Results</h2>
              <span className="text-gray-400 text-sm">
                {suite?.testSuiteName || "Suite"} #{suiteId}
              </span>
              <button
                onClick={handleGeneratePDF}
                disabled={isGeneratingPDF || endpointUrls.length === 0}
                className="ml-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium transition-colors"
              >
                {isGeneratingPDF ? (
                  <>
                    <Loader2Icon size={16} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Download PDF
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {endpointUrls.length} endpoint{endpointUrls.length !== 1 ? "s" : ""} tested
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50" ref={reportRef}>
        {endpointUrls.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Activity className="h-12 w-12 text-gray-300 mb-3" />
            <p className="text-gray-500 text-lg font-medium">No test results found</p>
            <p className="text-gray-400 text-sm mt-1">Run a performance test to see results here</p>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {endpointUrls?.map((url) => (
              <EndpointResultCard
                key={url}
                url={url}
                isExpanded={expandedEndpoints[url]}
                onToggle={() => toggleEndpoint(url)}
                results={selectedEndpoint === url ? endpointResults : null}
                isLoading={selectedEndpoint === url && loadingResults}
                formatDate={formatDate}
                getStatusColor={getStatusColor}
                getLoadProfileBadge={getLoadProfileBadge}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const EndpointResultCard = ({
  url,
  isExpanded,
  onToggle,
  results,
  isLoading,
  formatDate,
  getStatusColor,
  getLoadProfileBadge,
}) => {
  const allTests = results?.data ? Object.values(results.data).flat() : [];
  const latestTest = allTests.length > 0 ? allTests[0] : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      {/* Endpoint Header */}
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3 flex-1 text-left">
          {isExpanded ? (
            <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight size={20} className="text-gray-400 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="font-mono text-sm text-gray-900 truncate">{url}</p>
            {latestTest && (
              <p className="text-xs text-gray-500 mt-1">
                {allTests.length} test run{allTests.length !== 1 ? "s" : ""} • Last run:{" "}
                {formatDate(latestTest.startTime)}
              </p>
            )}
          </div>
        </div>
        {latestTest && (
          <div className="flex items-center gap-2 ml-4">
            <span
              className={`px-2.5 py-1 rounded text-xs font-semibold border ${getStatusColor(
                latestTest.status,
              )}`}
            >
              {latestTest.status}
            </span>
          </div>
        )}
      </button>

      {/* Expanded Results */}
      {isExpanded && (
        <div className="border-t border-gray-200 bg-gray-50">
          {isLoading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
              <p className="ml-3 text-gray-500 text-sm">Loading test results...</p>
            </div>
          ) : allTests.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-500 text-sm">No results available</p>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {allTests.map((test) => (
                <TestRunCard
                  key={test.id}
                  test={test}
                  formatDate={formatDate}
                  getStatusColor={getStatusColor}
                  getLoadProfileBadge={getLoadProfileBadge}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const TestRunCard = ({ test, formatDate, getStatusColor, getLoadProfileBadge }) => {
  const successRate =
    test?.totalRequests > 0
      ? ((test.successfulRequests / test.totalRequests) * 100).toFixed(2)
      : "0.00";

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      {/* Test Header */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-gray-900 mb-1">{test?.name || "Unnamed Test"}</h4>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <Calendar size={12} />
            <span>{test?.startTime ? formatDate(test.startTime) : "N/A"}</span>
            <span className="text-gray-300">→</span>
            <span>{test?.endTime ? formatDate(test.endTime) : "N/A"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-2.5 py-1 rounded text-xs font-semibold border ${getLoadProfileBadge(test?.loadProfile)}`}
          >
            {test?.loadProfile || "N/A"}
          </span>
          <span
            className={`px-2.5 py-1 rounded text-xs font-semibold border ${getStatusColor(test?.status)}`}
          >
            {test?.status || "N/A"}
          </span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <MetricCard
          icon={<Users size={14} />}
          label="Virtual Users"
          value={test?.virtualUsers || 0}
        />
        <MetricCard
          icon={<Clock size={14} />}
          label="Duration"
          value={`${test?.durationMinutes || 0}m`}
        />
        <MetricCard
          icon={<Activity size={14} />}
          label="Total Requests"
          value={(test?.totalRequests || 0).toLocaleString()}
        />
        <MetricCard
          icon={<Zap size={14} />}
          label="Req/sec"
          value={(test?.requestsPerSecond || 0).toFixed(2)}
        />
      </div>

      {/* Response Times */}
      <div className="border-t border-gray-100 pt-4 mb-4">
        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Response Times
        </h5>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <ResponseMetric label="Min" value={test?.minResponseTime} />
          <ResponseMetric label="Avg" value={test?.avgResponseTime} />
          <ResponseMetric label="P50" value={test?.p50ResponseTime} />
          <ResponseMetric label="P95" value={test?.p95ResponseTime} />
          <ResponseMetric label="P99" value={test?.p99ResponseTime} />
        </div>
      </div>

      {/* Success Rate */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={16} className="text-green-600" />
            <span className="text-sm font-medium text-gray-700">
              {(test?.successfulRequests || 0).toLocaleString()} successful
            </span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle size={16} className="text-red-600" />
            <span className="text-sm font-medium text-gray-700">
              {(test?.failedRequests || 0).toLocaleString()} failed
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Success Rate:</span>
            <span
              className={`font-bold ${
                parseFloat(successRate) >= 99
                  ? "text-green-600"
                  : parseFloat(successRate) >= 95
                    ? "text-yellow-600"
                    : "text-red-600"
              }`}
            >
              {successRate}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResponseMetric = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-gray-500 mb-1">{label}</span>
    <span className="text-sm font-semibold text-gray-900">
      {typeof value === "number" ? value.toFixed(0) : value || "N/A"}
      {typeof value === "number" && (
        <span className="text-xs text-gray-500 ml-1 font-normal">ms</span>
      )}
    </span>
  </div>
);

const MetricCard = ({ icon, label, value }) => (
  <div className="flex flex-col">
    <div className="flex items-center gap-1 text-gray-500 mb-1">
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </div>
    <div className="text-lg font-semibold text-gray-900">{value}</div>
  </div>
);

export default PerformanceResults;
